import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { getProfile, updateProfile } from "../../store/slices/userSlice";
import { logout, updateUserCountry } from "../../store/slices/authSlice";
import { getBalance } from "../../store/slices/walletSlice";
import Header from "../../components/Layout/Header";
import BottomNav from "../../components/Layout/BottomNav";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import Modal from "../../components/UI/Modal";
import Drawer from "../../components/UI/Drawer";
import Alert from "../../components/UI/Alert";
import globalStyle from "../../styles/globalStyle";
import {
  formatCurrency,
  formatNumber,
  formatDate,
  setGlobalCurrency,
  globalCurrency,
  getXrpPrice,
  setXrpPriceInUSD,
  getCurrencyRate,
  formatXRP,
} from "../../utils/formatters";
import { COUNTRIES, getCountryName } from "../../utils/countries";
import api from "../../services/api";

import {
  UserIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  CalendarIcon,
  Cog6ToothIcon,
  KeyIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowRightOnRectangleIcon,
  WalletIcon,
  GlobeAltIcon,
  PencilIcon,
  ClockIcon,
  InformationCircleIcon,
  ExclamationCircleIcon,
  FireIcon,
  ArrowPathIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";

interface TradingStatusData {
  active: boolean;
  daily_rate: string;
  estimated_daily_earnings: number;
  xrp_price: number;
  last_earning_date: string | null;
  min_balance_required: number;
  user_eligible: boolean;
}

const Profile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { profile, isLoading } = useSelector((state: RootState) => state.user);
  const { balance } = useSelector((state: RootState) => state.wallet);

  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const [tradingStatus, setTradingStatus] = useState<TradingStatusData | null>(
    null,
  );
  const [tradingLoading, setTradingLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [editForm, setEditForm] = useState({
    first_name: "",
    last_name: "",
    country: "",
  });

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const [selectedCurrency, setSelectedCurrency] = useState(globalCurrency);

  const [xrpPriceData, setXrpPriceData] = useState({
    price: getXrpPrice(globalCurrency),
    source: "cached" as "live" | "cached",
    lastUpdated: localStorage.getItem("xrp_price_updated") || null,
  });

  useEffect(() => {
    if (user) {
      dispatch(getProfile());
      dispatch(getBalance());
      loadTradingData();
    }
    // eslint-disable-next-line
  }, [user, dispatch]);

  useEffect(() => {
    if (profile) {
      setEditForm({
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        country: (profile as any).country || user?.country || "",
      });
    }
  }, [profile, user]);

  useEffect(() => {
    if (balance?.estimated_usd && user?.xrp_balance) {
      const calculatedXrpPriceUSD = balance.estimated_usd / user.xrp_balance;
      if (calculatedXrpPriceUSD > 0) {
        setXrpPriceInUSD(calculatedXrpPriceUSD);
        const rate = getCurrencyRate(selectedCurrency);
        setXrpPriceData({
          price: calculatedXrpPriceUSD * rate,
          source: "live",
          lastUpdated: Date.now().toString(),
        });
      }
    } else {
      setXrpPriceData({
        price: getXrpPrice(selectedCurrency),
        source: "cached",
        lastUpdated: localStorage.getItem("xrp_price_updated"),
      });
    }
  }, [balance, user, selectedCurrency]);

  const fetchTradingStatus = async (): Promise<TradingStatusData> => {
    const response = await api.get("/trading/status");
    let dailyRate = (response.data?.daily_rate || "0.15")
      .toString()
      .replace(/%/g, "");
    return {
      active: response.data?.active ?? true,
      daily_rate: dailyRate,
      estimated_daily_earnings:
        parseFloat(response.data?.estimated_daily_earnings) || 0,
      xrp_price: parseFloat(response.data?.xrp_price) || 2.1,
      last_earning_date: response.data?.last_earning_date || null,
      min_balance_required:
        parseFloat(response.data?.min_balance_required) || 10,
      user_eligible: response.data?.user_eligible ?? true,
    };
  };

  const loadTradingData = async () => {
    try {
      setTradingLoading(true);
      const status = await fetchTradingStatus();
      setTradingStatus(status);
      if (status.xrp_price) setXrpPriceInUSD(status.xrp_price);
    } catch {
      setTradingStatus(null);
    } finally {
      setTradingLoading(false);
    }
  };

  const refreshTradingData = async () => {
    setIsRefreshing(true);
    await loadTradingData();
    setIsRefreshing(false);
  };

  const handleEditProfile = async () => {
    try {
      const result = await dispatch(updateProfile(editForm)).unwrap();
      // Sync country back into auth state
      if (editForm.country) {
        dispatch(updateUserCountry(editForm.country));
      }
      setShowEditProfile(false);
      dispatch(getProfile());
    } catch (error: any) {
      console.error("Failed to update profile:", error);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("All fields are required");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return;
    }
    try {
      setPasswordLoading(true);
      setPasswordError(null);
      setPasswordSuccess(true);
      setTimeout(() => {
        setShowChangePassword(false);
        setPasswordSuccess(false);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }, 2000);
    } catch (error: any) {
      setPasswordError(error.message || "Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogout = async () => {
    await dispatch(logout());
    window.location.href = "/login";
  };

  const handleCurrencyChange = (currency: string) => {
    setSelectedCurrency(currency);
    setGlobalCurrency(currency);
    setXrpPriceData((prev) => ({ ...prev, price: getXrpPrice(currency) }));
    setShowPreferences(false);
  };

  const calculateXrpValue = () => (user?.xrp_balance || 0) * xrpPriceData.price;

  const getDailyEarnings = () => {
    const xrpBalance = user?.xrp_balance || 0;
    if (tradingStatus) {
      const rate = parseFloat(tradingStatus.daily_rate.replace(/%/g, "")) / 100;
      return (
        xrpBalance * rate * (tradingStatus.xrp_price || xrpPriceData.price)
      );
    }
    return xrpBalance * 0.0069 * xrpPriceData.price;
  };

  const getDailyEarningsXRP = () => {
    const xrpBalance = user?.xrp_balance || 0;
    if (tradingStatus) {
      const rate = parseFloat(tradingStatus.daily_rate.replace(/%/g, "")) / 100;
      return xrpBalance * rate;
    }
    return xrpBalance * 0.0069;
  };

  const formatLastUpdated = () => {
    if (!xrpPriceData.lastUpdated) return "Unknown";
    const diff = Math.floor(
      (Date.now() - parseInt(xrpPriceData.lastUpdated)) / 60000,
    );
    if (diff < 1) return "Just now";
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  const getIsActive = () => {
    const v = user?.is_active;
    if (v === false) return false;
    return true;
  };

  const resolvedKycStatus = (() => {
    const raw = user?.kyc_status;
    if (!raw || raw === "" || raw === "null") return null;
    return raw;
  })();

  const getKycBadgeClass = (status: string | null) => {
    switch (status) {
      case "approved":
        return globalStyle.badgeSuccess;
      case "rejected":
        return globalStyle.badgeDanger;
      case "pending":
        return globalStyle.badgeWarning;
      default:
        return globalStyle.badgeInfo;
    }
  };

  const getKycStatusLabel = (status: string | null) => {
    switch (status) {
      case "approved":
        return "Approved";
      case "rejected":
        return "Rejected";
      case "pending":
        return "Pending";
      default:
        return "Not Submitted";
    }
  };

  const getNextDistributionTime = () => {
    const now = new Date();
    const tomorrow = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1),
    );
    const diff = tomorrow.getTime() - now.getTime();
    return {
      hours: Math.floor(diff / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
    };
  };

  // Country display helper
  const userCountry = (profile as any)?.country || user?.country;

  return (
    <div className={globalStyle.container}>
      <Header />

      <main className="page-content">
        {/* Profile Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-full bg-primary-900 flex items-center justify-center">
                <UserIcon className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h1 className={globalStyle.h3}>
                  {user?.first_name} {user?.last_name}
                </h1>
                <p className={globalStyle.body2 + " text-gray-400"}>
                  @{user?.username}
                </p>
                {userCountry && (
                  <p className="text-sm text-gray-500 flex items-center mt-0.5">
                    <MapPinIcon className="w-3.5 h-3.5 mr-1" />
                    {getCountryName(userCountry)}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowEditProfile(true)}
            >
              <PencilIcon className="w-4 h-4 mr-2 text-primary" />
              Edit Profile
            </Button>
          </div>
        </div>

        {/* Account Information */}
        <Card className="mb-6" padding="lg">
          <h3 className={globalStyle.h5}>Account Information</h3>
          <div className="mt-4 space-y-4">
            {/* Email */}
            <div className="flex items-center justify-between py-3 border-b border-gray-800">
              <div className="flex items-center">
                <EnvelopeIcon className="w-5 h-5 text-gray-500 mr-3" />
                <div>
                  <p className="text-sm text-gray-400">Email Address</p>
                  <p className="font-medium text-white">{user?.email}</p>
                </div>
              </div>
              <span
                className={`${globalStyle.badge} ${user?.email_verified ? globalStyle.badgeSuccess : globalStyle.badgeWarning}`}
              >
                {user?.email_verified ? "Verified" : "Not Verified"}
              </span>
            </div>

            {/* Country */}
            <div className="flex items-center justify-between py-3 border-b border-gray-800">
              <div className="flex items-center">
                <MapPinIcon className="w-5 h-5 text-gray-500 mr-3" />
                <div>
                  <p className="text-sm text-gray-400">Country</p>
                  <p className="font-medium text-white">
                    {userCountry ? getCountryName(userCountry) : "Not set"}
                  </p>
                </div>
              </div>
              {!userCountry && (
                <button
                  onClick={() => setShowEditProfile(true)}
                  className="text-xs text-primary hover:text-primary-300 underline"
                >
                  Add country
                </button>
              )}
            </div>

            {/* KYC Status */}
            <div className="flex items-center justify-between py-3 border-b border-gray-800">
              <div className="flex items-center">
                <ShieldCheckIcon className="w-5 h-5 text-gray-500 mr-3" />
                <div>
                  <p className="text-sm text-gray-400">KYC Status</p>
                  <p className="font-medium text-white capitalize">
                    {getKycStatusLabel(resolvedKycStatus)}
                  </p>
                </div>
              </div>
              <span
                className={`${globalStyle.badge} ${getKycBadgeClass(resolvedKycStatus)}`}
              >
                {getKycStatusLabel(resolvedKycStatus)}
              </span>
            </div>

            {/* Account Type */}
            <div className="flex items-center justify-between py-3 border-b border-gray-800">
              <div className="flex items-center">
                <Cog6ToothIcon className="w-5 h-5 text-gray-500 mr-3" />
                <div>
                  <p className="text-sm text-gray-400">Account Type</p>
                  <p className="font-medium text-white capitalize">
                    {user?.role}
                  </p>
                </div>
              </div>
              <span
                className={`${globalStyle.badge} ${getIsActive() ? globalStyle.badgeSuccess : globalStyle.badgeDanger}`}
              >
                {getIsActive() ? "Active" : "Inactive"}
              </span>
            </div>

            {/* XRP Balance */}
            <div className="flex items-center justify-between py-3 border-b border-gray-800">
              <div className="flex items-center">
                <WalletIcon className="w-5 h-5 text-gray-500 mr-3" />
                <div>
                  <p className="text-sm text-gray-400">XRP Balance</p>
                  <p className="font-medium text-white">
                    {formatXRP(user?.xrp_balance || 0)} XRP
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium text-white">
                  {formatCurrency(calculateXrpValue())}
                </p>
                <p className="text-xs text-gray-500">
                  1 XRP = {formatCurrency(xrpPriceData.price)}
                  <span className="block text-xs text-gray-400 mt-0.5">
                    ({xrpPriceData.source === "live" ? "Live" : "Cached"} •{" "}
                    {formatLastUpdated()})
                  </span>
                </p>
              </div>
            </div>

            {/* Member Since */}
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center">
                <CalendarIcon className="w-5 h-5 text-gray-500 mr-3" />
                <div>
                  <p className="text-sm text-gray-400">Member Since</p>
                  <p className="font-medium text-white">
                    {user?.created_at ? formatDate(user.created_at) : "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Settings Grid */}
        <div className={`${globalStyle.grid2} mb-6`}>
          <Card
            hover
            padding="lg"
            className="text-center cursor-pointer"
            onClick={() => setShowChangePassword(true)}
          >
            <div className="w-12 h-12 rounded-full bg-blue-900 flex items-center justify-center mx-auto mb-4">
              <KeyIcon className="w-6 h-6 text-blue-300" />
            </div>
            <h4 className={globalStyle.h6}>Change Password</h4>
            <p className={`${globalStyle.caption} mt-2 text-gray-400`}>
              Update your account password
            </p>
          </Card>

          <Card
            hover
            padding="lg"
            className="text-center cursor-pointer"
            onClick={() => setShowPreferences(true)}
          >
            <div className="w-12 h-12 rounded-full bg-purple-900 flex items-center justify-center mx-auto mb-4">
              <GlobeAltIcon className="w-6 h-6 text-purple-300" />
            </div>
            <h4 className={globalStyle.h6}>Currency</h4>
            <p className={`${globalStyle.caption} mt-2 text-gray-400`}>
              Set display currency ({selectedCurrency})
            </p>
          </Card>
        </div>

        {/* Trading Information */}
        <Card className="mb-6" padding="lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className={globalStyle.h5}>AI Trading Bot Information</h3>
            <Button
              variant="tertiary"
              size="sm"
              onClick={refreshTradingData}
              loading={isRefreshing}
            >
              <ArrowPathIcon className="w-4 h-4" />
            </Button>
          </div>

          {tradingLoading ? (
            <div className="text-center py-8">
              <svg
                className={`${globalStyle.loadingSpinner} mx-auto`}
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className={`${globalStyle.body2} mt-4`}>
                Loading trading information...
              </p>
            </div>
          ) : tradingStatus ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div
                    className={`w-2 h-2 rounded-full animate-pulse mr-2 ${tradingStatus.active ? "bg-green-500" : "bg-red-500"}`}
                  />
                  <span className={globalStyle.body2}>Bot Status</span>
                </div>
                <span
                  className={`${globalStyle.badge} ${tradingStatus.active ? globalStyle.badgeSuccess : globalStyle.badgeDanger}`}
                >
                  {tradingStatus.active ? "Active" : "Inactive"}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <InformationCircleIcon className="w-4 h-4 text-blue-500 mr-2" />
                  <span className={globalStyle.body2}>Daily Rate</span>
                </div>
                <span className="font-bold text-green-500">
                  {tradingStatus.daily_rate.replace(/%/g, "")}%
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FireIcon className="w-4 h-4 text-orange-500 mr-2" />
                  <span className={globalStyle.body2}>Daily Earnings</span>
                </div>
                <div className="text-right">
                  <p className="font-medium text-green-500">
                    {formatCurrency(getDailyEarnings())}
                  </p>
                  <p className={`${globalStyle.caption} text-gray-400`}>
                    {formatXRP(getDailyEarningsXRP())} XRP
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className={globalStyle.body2}>XRP Price</span>
                <span className="font-medium">
                  {formatCurrency(
                    tradingStatus.xrp_price || xrpPriceData.price,
                  )}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <ClockIcon className="w-4 h-4 text-purple-500 mr-2" />
                  <span className={globalStyle.body2}>Next Distribution</span>
                </div>
                <div className="text-right">
                  <span className="font-medium">00:00 UTC</span>
                  <p className={`${globalStyle.caption} text-gray-400`}>
                    {(() => {
                      const { hours, minutes } = getNextDistributionTime();
                      return `${hours}h ${minutes}m`;
                    })()}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className={globalStyle.body2}>Min Balance Required</span>
                <span className="font-medium">
                  {formatXRP(tradingStatus.min_balance_required || 0)} XRP
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className={globalStyle.body2}>Your Eligibility</span>
                <span
                  className={`${globalStyle.badge} ${tradingStatus.user_eligible ? globalStyle.badgeSuccess : globalStyle.badgeWarning}`}
                >
                  {tradingStatus.user_eligible ? "✓ Eligible" : "Not Eligible"}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <ExclamationCircleIcon className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className={globalStyle.body2}>
                Unable to load trading information
              </p>
              <Button
                variant="tertiary"
                size="sm"
                className="mt-2"
                onClick={refreshTradingData}
              >
                Retry
              </Button>
            </div>
          )}
        </Card>

        <div className="mb-6">
          <Button
            variant="secondary"
            fullWidth
            onClick={() => {
              dispatch(getBalance());
              refreshTradingData();
            }}
            loading={isLoading || isRefreshing}
          >
            <ArrowTrendingUpIcon className="w-5 h-5 mr-2 text-primary" />
            Refresh All Data
          </Button>
        </div>

        <div className="mt-6">
          <Button
            variant="danger"
            fullWidth
            onClick={() => setShowLogoutConfirm(true)}
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5 mr-2" />
            Logout
          </Button>
        </div>
      </main>

      <BottomNav />

      {/* Change Password Modal */}
      <Modal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        title="Change Password"
        size="md"
      >
        <div className="space-y-6">
          {passwordError && (
            <Alert
              type="error"
              message={passwordError}
              onClose={() => setPasswordError(null)}
            />
          )}
          {passwordSuccess && (
            <Alert type="success" message="Password changed successfully!" />
          )}
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className={globalStyle.input}
              placeholder="Enter current password"
            />
          </div>
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={globalStyle.input}
              placeholder="Enter new password"
            />
          </div>
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={globalStyle.input}
              placeholder="Confirm new password"
            />
          </div>
          <div className="bg-yellow-900 border border-yellow-800 rounded-lg p-4">
            <h4 className="text-sm font-medium text-yellow-300 mb-2">
              Password Requirements
            </h4>
            <ul className="text-sm text-yellow-200 space-y-1">
              <li>• At least 8 characters long</li>
              <li>• Include uppercase and lowercase letters</li>
              <li>• Include at least one number</li>
              <li>• Include at least one special character</li>
            </ul>
          </div>
          <div className="flex space-x-3">
            <Button
              variant="tertiary"
              onClick={() => setShowChangePassword(false)}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangePassword}
              loading={passwordLoading}
              disabled={!currentPassword || !newPassword || !confirmPassword}
              fullWidth
            >
              Change Password
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Profile Modal */}
      <Modal
        isOpen={showEditProfile}
        onClose={() => setShowEditProfile(false)}
        title="Edit Profile"
        size="md"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className={globalStyle.formGroup}>
              <label className={globalStyle.label}>First Name</label>
              <input
                type="text"
                value={editForm.first_name}
                onChange={(e) =>
                  setEditForm({ ...editForm, first_name: e.target.value })
                }
                className={globalStyle.input}
              />
            </div>
            <div className={globalStyle.formGroup}>
              <label className={globalStyle.label}>Last Name</label>
              <input
                type="text"
                value={editForm.last_name}
                onChange={(e) =>
                  setEditForm({ ...editForm, last_name: e.target.value })
                }
                className={globalStyle.input}
              />
            </div>
          </div>

          {/* Country select */}
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Country</label>
            <select
              value={editForm.country}
              onChange={(e) =>
                setEditForm({ ...editForm, country: e.target.value })
              }
              className={globalStyle.input}
            >
              <option value="">— Select your country —</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.flag} {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Username</label>
            <input
              type="text"
              value={user?.username || ""}
              className={globalStyle.input}
              disabled
            />
            <p className="text-xs text-gray-400 mt-1">
              Username cannot be changed
            </p>
          </div>

          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Email</label>
            <input
              type="email"
              value={user?.email || ""}
              className={globalStyle.input}
              disabled
            />
            <p className="text-xs text-gray-400 mt-1">
              Email cannot be changed
            </p>
          </div>

          <div className="flex space-x-3 pt-4">
            <Button
              variant="tertiary"
              onClick={() => setShowEditProfile(false)}
              fullWidth
            >
              Cancel
            </Button>
            <Button onClick={handleEditProfile} fullWidth>
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      {/* Currency Preferences Drawer */}
      <Drawer
        isOpen={showPreferences}
        onClose={() => setShowPreferences(false)}
        title="Currency Settings"
        size="md"
      >
        <div className="space-y-6">
          <div>
            <h4 className={`${globalStyle.h6} mb-3`}>
              Select Display Currency
            </h4>
            <div className="space-y-2">
              {["USD", "EUR", "GBP", "JPY"].map((currency) => (
                <div
                  key={currency}
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${selectedCurrency === currency ? "border-primary bg-primary-900" : "border-gray-700 hover:bg-gray-800"}`}
                  onClick={() => handleCurrencyChange(currency)}
                >
                  <div className="flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full ${selectedCurrency === currency ? "bg-primary-800" : "bg-gray-800"} flex items-center justify-center mr-3`}
                    >
                      <CurrencyDollarIcon
                        className={`w-4 h-4 ${selectedCurrency === currency ? "text-primary" : "text-gray-400"}`}
                      />
                    </div>
                    <div>
                      <p className="font-medium text-white">{currency}</p>
                      <p className="text-sm text-gray-500">
                        {currency === "USD"
                          ? "US Dollar"
                          : currency === "EUR"
                            ? "Euro"
                            : currency === "GBP"
                              ? "British Pound"
                              : "Japanese Yen"}
                      </p>
                    </div>
                  </div>
                  {selectedCurrency === currency && (
                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="3"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
            <h4 className="text-sm font-medium text-gray-300 mb-3">
              Current Balance Preview
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">XRP Balance:</span>
                <span className="font-medium text-white">
                  {formatXRP(user?.xrp_balance || 0)} XRP
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">
                  Value in {selectedCurrency}:
                </span>
                <span className="font-medium text-white">
                  {formatCurrency(calculateXrpValue())}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">XRP Price:</span>
                <span className="font-medium text-white">
                  1 XRP = {formatCurrency(xrpPriceData.price)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Drawer>

      {/* Logout Confirm Modal */}
      <Modal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        title="Confirm Logout"
        size="sm"
      >
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-red-900 flex items-center justify-center mx-auto mb-4">
              <ArrowRightOnRectangleIcon className="w-8 h-8 text-red-300" />
            </div>
            <h3 className={globalStyle.h5}>Logout?</h3>
            <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
              Are you sure you want to logout from your account?
            </p>
          </div>
          <div className="flex space-x-3">
            <Button
              variant="tertiary"
              onClick={() => setShowLogoutConfirm(false)}
              fullWidth
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleLogout} fullWidth>
              Logout
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Profile;
